import Delta from 'quill-delta'

interface ActionFieldValue {
  id?: number | null
  old_value?: unknown
  new_value?: unknown
  file_name?: string | null
  [key: string]: unknown
}

export type ActionFieldRecord = Record<string, ActionFieldValue>

function looksLikeJson(value: string) {
  const trimmed = value.trim()
  return (
    (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
    (trimmed.startsWith('[') && trimmed.endsWith(']')) ||
    (trimmed.startsWith('"') && trimmed.endsWith('"'))
  )
}

function normalizeActionValue(value: unknown): unknown {
  if (typeof value === 'string') {
    if (!looksLikeJson(value)) return value

    try {
      const parsed = JSON.parse(value)
      return normalizeActionValue(parsed)
    } catch {
      return value
    }
  }

  if (Array.isArray(value)) {
    return value.map(entry => normalizeActionValue(entry))
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [
        key,
        normalizeActionValue(item),
      ])
    )
  }

  return value
}

export function parseActionFields(value: unknown): ActionFieldRecord {
  const normalizedRoot = normalizeActionValue(value)

  if (
    !normalizedRoot ||
    typeof normalizedRoot !== 'object' ||
    Array.isArray(normalizedRoot)
  ) {
    return {}
  }

  const entries = Object.entries(normalizedRoot).map(
    ([field, rawFieldValue]) => {
      if (
        rawFieldValue &&
        typeof rawFieldValue === 'object' &&
        !Array.isArray(rawFieldValue)
      ) {
        return [field, rawFieldValue as ActionFieldValue]
      }

      return [
        field,
        {
          old_value: null,
          new_value: rawFieldValue,
        } as ActionFieldValue,
      ]
    }
  )

  return Object.fromEntries(entries)
}

export function tryBuildQuillDelta(value: unknown): Delta | null {
  const normalized = normalizeActionValue(value)

  if (Array.isArray(normalized)) {
    const looksLikeOps = normalized.every(
      op => op && typeof op === 'object' && 'insert' in op
    )
    if (looksLikeOps) return new Delta(normalized)
    return null
  }

  if (normalized && typeof normalized === 'object') {
    const ops = (normalized as { ops?: unknown }).ops
    if (Array.isArray(ops)) return new Delta(ops)
  }

  return null
}

export function quillDeltaToPlainText(delta: Delta) {
  const ops = (delta as { ops?: Array<{ insert?: unknown }> }).ops ?? []
  const text = ops
    .map(op => (typeof op.insert === 'string' ? op.insert : ''))
    .join('')
    .replace(/\s+/g, ' ')
    .trim()
  return text || '-'
}
